/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AbstractFormComponent,
  CommonActions,
  ContactDetailsDialogConfig,
  contactDetailsDialogRoute,
  FormActionPipelines,
  FormFlowState,
  PbHousePlanFinderDialogConfig,
  pbHousePlanFinderDialogRoute,
  PbHousePlanFinderListItem,
} from '@gh/builder-shared';
import { ContactType, validateSiteAddressInfo } from '@gh/builder-shared-data';
import { ActionFlow, AppRouter, getSingleNewSelectedEntity, UserContext } from '@gh/common-shared';
import { AutocompleteItem } from '@gh/common-shared-data';
import { MessageBasket } from '@gh/core-messages';
import {
  AddressAutocompleteComponent,
  AddressComponents,
  AutocompleteLoadMoreEvent,
  DialogService,
  foldAddressComponentsForXero,
  MapboxService,
  TinymceSettings,
  tmSettingsBuilder,
} from '@gh/core-ui';
import { foldToAsciiForXeroTrackingInputTrimmed } from '@gh/core-util';
import { filter$, map$ } from '@gh/rx/operators';
import { JobQuickCreateResponse } from '../../';
import { JobQuickDetailsBusinessService } from '../../resources/job-quick-details-business.service';
import { JobCustomer, JobQuickDetails } from '../../resources/job.model';
import { JobQuickDetailsFormState } from './job-quick-details-form.service';

export type JobQuickDetailsSaveFlowState = FormFlowState<JobQuickDetails> & { saveResponse: JobQuickCreateResponse };

@Component({
  standalone: false,
  selector: 'gh-job-quick-details',
  templateUrl: 'job-quick-details.component.html',
  styleUrls: ['job-quick-details.component.scss'],
  providers: [JobQuickDetailsFormState],
})
export class JobQuickDetailsComponent extends AbstractFormComponent<JobQuickDetails> implements OnInit {
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent | undefined;

  tmSettings: TinymceSettings;
  mapCoordinates: number[] | null;
  isUserDragUpdate: boolean;
  unitsCountList: number[] = [];
  countOfUnit = 15;
  showManualAddressFields = false;

  constructor(private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    public form: JobQuickDetailsFormState,
    private userContext: UserContext,
    private business: JobQuickDetailsBusinessService,
    private dialogs: DialogService,
    private router: AppRouter,
    private mapboxService: MapboxService) {
    super();
    for (let i = 0; i < this.countOfUnit; i++) {
      this.unitsCountList.push(i + 1);
    }
  }

  get completeFlow(): ActionFlow<JobQuickDetailsSaveFlowState> {
    if (this.form.group.invalid) {
      this.checkAndShowManualAddressFields();
      this.form.group.markAllAsTouched();
    }
    return this.pipelines.complete<JobQuickDetailsSaveFlowState>({
      successMessage: (state) => ({ id: 'msg_quick_job_created', params: { number: state.saveResponse.jobNumber } }),
      validate: this.commonActions.validate((state) => this.validateBeforeProcess(state.value)),
      execute: this.commonActions.async((state) => this.business.create(state.value).pipe(
        map$((saveResponse) => ({ ...state, saveResponse })))),
      finish: () => this.router.navigateByUrl('/job-admin/jobs'),
    });
  }

  ngOnInit(): void {
    this.form.build();

    this.tmSettings = tmSettingsBuilder().scayt().height(200).statusBar(false).build();

    const hasAddressData = !!(this.form.group.get('suburb')?.value ||
      this.form.group.get('streetNumber')?.value ||
      this.form.group.get('street')?.value ||
      this.form.group.get('postCode')?.value);
    this.showManualAddressFields = hasAddressData;
  }

  loadMoreCustomers(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findCustomersByName(event.query));
  }

  loadMoreSalesPersons(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findSalesPersonsByName(event.query));
  }

  loadMorePriceBooks(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findPriceBooksByName(event.query));
  }

  loadMoreBaseDesigns(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findAllBaseDesignsByName(event.query));
  }

  onCustomerChange(contact: JobCustomer): void {
    this.form.updateContact(contact);
  }

  onPriceBookChange(pb: AutocompleteItem): void {
    this.form.updatePriceBook(pb);
  }

  onOpenCustomer(): void {
    const { contact } = this.form;
    if (contact) {
      this.openContact(contact);
    }
  }

  onCreateCustomer(): void {
    this.openContact();
  }

  onSearchHousePlan(): void {
    const { priceBook, housePlan, isQqShow } = this.form;
    if (priceBook) {
      this.dialogs.openWithInput(pbHousePlanFinderDialogRoute, <PbHousePlanFinderDialogConfig>{
        multiselection: false,
        preselected: housePlan ? [housePlan.housePlanId] : [],
        priceBookId: priceBook.id,
        priceBookName: priceBook.name,
        filter: {
          isSalesEstimate: true,
          isQuickQuote: isQqShow,
        },
      }).afterClosed().pipe(
        filter$(Boolean),
        map$(getSingleNewSelectedEntity))
        .subscribe((hp: PbHousePlanFinderListItem) => {
          this.form.setHousePlan(hp);
        });
    }
  }

  onClearHousePlan(): void {
    this.form.clearHousePlan();
  }

  onAddressChange() {
    const streetNumber = this.form.group.get('streetNumber')?.value || '';
    const street = this.form.group.get('street')?.value || '';
    const city = this.form.group.get('suburb')?.value || '';
    const postCode = this.form.group.get('postCode')?.value || '';

    const addressParts: string[] = [];
    if (streetNumber) addressParts.push(streetNumber);
    if (street) addressParts.push(street);
    if (city) addressParts.push(city);
    if (postCode) addressParts.push(postCode);

    const newAddress = addressParts.join(', ');

    if (this.addressAutocomplete) {
      this.addressAutocomplete.inputValue = newAddress;
    }

    const latitudeControl = this.form.group.get('latitude');
    const longitudeControl = this.form.group.get('longitude');

    if (latitudeControl && longitudeControl &&
      latitudeControl.valid && longitudeControl.valid) {
      this.mapCoordinates = [longitudeControl.value, latitudeControl.value];
    } else {
      this.mapCoordinates = null;
    }
  }

  onCoordinatesChanged(coordinates: [number, number]): void {
    this.mapboxService.reverseGeocode(coordinates).subscribe(result => {
      const feature = result?.features?.[0];
      if (!feature) return;
      const components = this.mapboxService.getUpdatedComponents(result);
      components.coordinates = coordinates.map(c => +c.toFixed(6)) as [number, number];
      this.onAddressSelected(components, true);
      if (this.addressAutocomplete) {
        this.addressAutocomplete.inputValue = foldToAsciiForXeroTrackingInputTrimmed(feature.place_name);
      }
    });
  }

  onAddressSelected(components: AddressComponents, isUserDrag: boolean = false): void {
    if (!components) return;

    const c = foldAddressComponentsForXero(components);

    this.form.group.patchValue({
      streetNumber: c.streetNo || '',
      street: c.street || '',
      suburb: c.city || '',
      postCode: c.postalCode || '',
    });
    if (c.coordinates) {
      const [lng, lat] = c.coordinates;
      const latitudeControl = this.form.group.get('latitude');
      const longitudeControl = this.form.group.get('longitude');

      if (latitudeControl && longitudeControl) {
        latitudeControl.setValue(lat);
        longitudeControl.setValue(lng);

        if (latitudeControl.valid && longitudeControl.valid) {
          this.mapCoordinates = c.coordinates;
          this.isUserDragUpdate = isUserDrag;
        } else {
          this.mapCoordinates = null;
          latitudeControl.setValue(null);
          longitudeControl.setValue(null);
        }
      }
    }

    ['streetNumber', 'street', 'suburb', 'postCode', 'latitude', 'longitude'].forEach(field => {
      const control = this.form.group.get(field);
      if (control) control.markAsDirty();
    });
  }

  private openContact(openedContact?: JobCustomer): void {
    this.dialogs.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      contactType: ContactType.Customer,
      contactId: openedContact ? openedContact.contactId : void 0,
      parentTitle: 'lbl_quick_job_create',
      entryPoint: 'quick-job',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((contact: any) => {
        this.form.setContact(contact);
      });
  }

  private validateBeforeProcess(site: JobQuickDetails): Maybe<MessageBasket> {
    const errorId = validateSiteAddressInfo(site);
    return errorId ? MessageBasket.error(errorId) : void 0;
  }

  private checkAndShowManualAddressFields(): void {
    if (this.showManualAddressFields) return;

    const addressFields = ['streetNumber', 'street', 'suburb', 'postCode', 'latitude', 'longitude'];
    const hasAddressErrors = addressFields.some(field => {
      const control = this.form.group.get(field);
      return control && control.invalid;
    });

    if (hasAddressErrors) {
      this.showManualAddressFields = true;
    }
  }
}
